import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { API_BASE_URL } from '../../../utils/apiConfig';
import toast from 'react-hot-toast';
import Icon from '../design/Icon';
import '../design/member.css';

/* Selective account deletion (work order A6/B5): the member picks what goes —
   personal data (anonymisation) and/or leaving all gyms. Both = full deletion. */

type Scope = 'personal_data' | 'gym_memberships';

const MemberDeleteAccountPage: React.FC = () => {
  const { token, signOut } = useMemberAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState<{ status: string; requestedAt: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [scopes, setScopes] = useState<Scope[]>(['personal_data', 'gym_memberships']);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/app/member/deletion-request`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setRequest(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const toggleScope = (s: Scope) =>
    setScopes(prev => (prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]));

  const handleSubmit = async () => {
    if (!token || scopes.length === 0) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/app/member/deletion-request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scopes }),
      });
      const data = await res.json();
      if (data.success) {
        setRequest(data.data);
        toast.success('Deletion request submitted.');
      } else {
        toast.error(data.error || 'Failed to submit');
      }
    } catch {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const personalData = scopes.includes('personal_data');
  const gymMemberships = scopes.includes('gym_memberships');

  const scopeRow = (
    checked: boolean,
    onToggle: () => void,
    title: string,
    lines: string[],
    note?: React.ReactNode
  ) => (
    <button
      className="card"
      style={{
        textAlign: 'left',
        borderColor: checked ? 'var(--clay)' : 'var(--line)',
        background: checked ? 'var(--clay-soft)' : 'var(--card)',
      }}
      onClick={onToggle}
    >
      <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
        <div
          style={{
            width: 22, height: 22, flex: '0 0 22px', borderRadius: 7, marginTop: 1,
            border: `1.5px solid ${checked ? 'var(--clay)' : 'var(--line-2)'}`,
            background: checked ? 'var(--clay)' : 'transparent',
            display: 'grid', placeItems: 'center',
          }}
        >
          {checked && <Icon n="check" s={14} c="var(--card)" />}
        </div>
        <div className="col" style={{ gap: 4, minWidth: 0 }}>
          <div className="mid">{title}</div>
          {lines.map(l => (
            <div key={l} className="tiny wrap">{l}</div>
          ))}
          {note}
        </div>
      </div>
    </button>
  );

  return (
    <div className="mapp">
      <div className="mpage nofoot">
        <div className="row" style={{ paddingTop: 8, gap: 10 }}>
          <button className="icon-btn" aria-label="Back to profile" onClick={() => navigate('/app/profile')}>
            <Icon n="left" s={18} c="var(--ink-2)" />
          </button>
          <div className="col" style={{ gap: 1, minWidth: 0 }}>
            <div className="dp dp-m">Delete account & data</div>
            <div className="tiny">Choose what should be removed</div>
          </div>
        </div>

        {loading ? (
          <div className="skel" style={{ height: 180 }} />
        ) : request?.status === 'pending' ? (
          <div className="card flat" style={{ borderColor: 'var(--gold-line)' }}>
            <div className="lbl-d">Request pending</div>
            <p className="sub wrap" style={{ marginTop: 8, lineHeight: 1.5 }}>
              Your deletion request was received on{' '}
              <b>{new Date(request.requestedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</b>.
              It will be processed within <b>24–48 hours</b>.
            </p>
            <p className="tiny wrap" style={{ marginTop: 8 }}>If you have questions, please contact your gym directly.</p>
          </div>
        ) : request?.status === 'processed' ? (
          <div className="card flat">
            <div className="lbl">Data removed</div>
            <p className="sub wrap" style={{ marginTop: 8 }}>Your account data has already been deleted.</p>
            <button className="btn ghost sm" style={{ marginTop: 12 }} onClick={signOut}>Sign out</button>
          </div>
        ) : (
          <>
            {scopeRow(
              personalData,
              () => toggleScope('personal_data'),
              'Delete my personal data',
              [
                'Name, phone number, email, health profile, progress photos and reports.',
                'Bookings, check-ins and billing records stay, anonymised.',
              ],
              <div className="tiny wrap" style={{ marginTop: 2, color: 'var(--ink-2)' }}>
                Only worried about coaches seeing your progress? You can{' '}
                <button
                  style={{ textDecoration: 'underline', fontWeight: 600 }}
                  onClick={e => { e.stopPropagation(); navigate('/app/profile'); }}
                >
                  turn off progress sharing
                </button>{' '}
                instead — no deletion needed.
              </div>
            )}
            {scopeRow(
              gymMemberships,
              () => toggleScope('gym_memberships'),
              'Remove me from my gyms',
              ['You leave every gym you belong to. Your account itself stays until you also delete your personal data.']
            )}

            <div className="card flat" style={{ borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
              <p className="sub wrap" style={{ color: 'var(--clay)', lineHeight: 1.5 }}>
                {personalData
                  ? <>The selected data will be permanently removed within <b>24–48 hours</b>. This cannot be reversed.</>
                  : <>You will be removed from your gyms within <b>24–48 hours</b>. Rejoining later needs the front desk.</>}
              </p>
            </div>

            <div className="col" style={{ gap: 10, marginTop: 'auto', paddingTop: 12 }}>
              <button
                className="btn block"
                style={{ background: 'var(--clay)', color: 'var(--card)', opacity: scopes.length === 0 ? 0.5 : 1 }}
                onClick={handleSubmit}
                disabled={submitting || scopes.length === 0}
              >
                {submitting ? 'Submitting…' : scopes.length === 0 ? 'Pick what to delete' : 'Submit deletion request'}
              </button>
              <button className="btn ghost block" onClick={() => navigate('/app/profile')}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MemberDeleteAccountPage;
